import { createRouter, createWebHistory } from 'vue-router'
import { useAuth } from '@/composables/useAuth'
// Landing stays in the main chunk; every other page loads on demand
import LandingView from '@/views/LandingView.vue'

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  // Anchors (e.g. /how-it-works#formats) below the fixed 64px navbar; back/forward restores position
  scrollBehavior(to, _from, savedPosition) {
    if (savedPosition) return savedPosition
    if (to.hash) return { el: to.hash, top: 72, behavior: 'smooth' }
    return { top: 0 }
  },
  routes: [
    {
      path: '/',
      name: 'landing',
      component: LandingView,
    },
    {
      path: '/how-it-works',
      name: 'how-it-works',
      component: () => import('@/views/HowItWorksView.vue'),
    },
    {
      path: '/pricing',
      name: 'pricing',
      component: () => import('@/views/PricingView.vue'),
    },
    {
      path: '/legal',
      name: 'legal',
      component: () => import('@/views/LegalView.vue'),
    },
    {
      path: '/upload',
      name: 'upload',
      component: () => import('@/views/UploadView.vue'),
      // Public: guests get an anonymous session when they drop a file
    },
    {
      path: '/files',
      name: 'files',
      component: () => import('@/views/FilesView.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/account',
      name: 'account',
      component: () => import('@/views/AccountView.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/viewer/:fileId',
      name: 'viewer',
      component: () => import('@/views/ViewerView.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/s/:shareId',
      name: 'shared-viewer',
      component: () => import('@/views/SharedViewerView.vue'),
    },
    // Dev-only viewer harness (tree-shaken out of production builds)
    ...(import.meta.env.DEV
      ? [{ path: '/dev/viewer', name: 'dev-viewer', component: () => import('@/views/DevViewerView.vue') }]
      : []),
    {
      // OAuth consent page for the MCP server (Claude connectors)
      path: '/oauth/authorize',
      name: 'oauth-authorize',
      component: () => import('@/views/OAuthAuthorizeView.vue'),
    },
    // Redirect old routes
    { path: '/login', redirect: '/' },
    { path: '/home', redirect: '/upload' },
  ],
})

router.beforeEach(async (to) => {
  const { currentUser, isLoading } = useAuth()

  // Wait for auth to initialize
  if (isLoading.value) {
    await new Promise<void>((resolve) => {
      const unwatch = setInterval(() => {
        if (!isLoading.value) {
          clearInterval(unwatch)
          resolve()
        }
      }, 50)
    })
  }

  if (to.meta.requiresAuth && !currentUser.value) {
    return { name: 'landing' }
  }
})

export default router
